import React, { createContext, useCallback, useContext, useState } from "react";
import { Snackbar, Slide, IconButton, Box, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";

const SEVERITY_CONFIG = {
  success: {
    icon: CheckCircleOutlineIcon,
    color: "#10b981",
  },
  error: {
    icon: ErrorOutlineIcon,
    color: "#ef4444",
  },
  warning: {
    icon: WarningAmberIcon,
    color: "#f59e0b",
  },
  info: {
    icon: InfoOutlinedIcon,
    color: "#6366f1",
  },
};

function SlideUp(props) {
  return <Slide {...props} direction="up" />;
}

export function StyledSnackbar({ open, message, severity = "success", onClose, autoHideDuration = 3000 }) {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.success;
  const Icon = config.icon;

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={(_, reason) => {
        if (reason !== "clickaway") onClose?.();
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      TransitionComponent={SlideUp}
      sx={{ zIndex: 9999 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: 240,
          maxWidth: 480,
          px: 2,
          py: 1.25,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "10px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1.5px 6px rgba(0,0,0,0.06)",
        }}
      >
        <Icon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "text.primary",
            lineHeight: 1.45,
            flex: 1,
            minWidth: 0,
          }}
        >
          {message}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            p: 0.25,
            ml: 0.5,
            flexShrink: 0,
            color: "text.disabled",
            "&:hover": { color: "text.secondary", bgcolor: "action.hover" },
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </Snackbar>
  );
}

export function useSnackbar() {
  const [state, setState] = useState({ open: false, message: "", severity: "success" });

  const show = useCallback((message, severity = "success") => {
    setState({ open: true, message, severity });
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const snackbarProps = {
    open: state.open,
    message: state.message,
    severity: state.severity,
    onClose: close,
  };

  return { show, close, snackbarProps };
}

const SnackbarContext = createContext(null);

export function SnackbarProvider({ children }) {
  const snackbar = useSnackbar();

  return (
    <SnackbarContext.Provider value={snackbar.show}>
      {children}
      <StyledSnackbar {...snackbar.snackbarProps} />
    </SnackbarContext.Provider>
  );
}

export function useGlobalSnackbar() {
  const show = useContext(SnackbarContext);
  if (!show) throw new Error("useGlobalSnackbar must be used within SnackbarProvider");
  return show;
}
